import type { TDocumentation } from 'src/modules/conversations/components/types';
import { AWS_S3 } from 'src/constants/crm/aws';
import { uploadImageToS3 } from 'src/contexts/utils/uploadImageToS3';
import { generateFileKey } from 'src/modules/conversations/helpers/generateFileKey';
import type { TOutboundMultimediaItem } from '../types/outboundMultimedia';
import {
    getDocumentationContentType,
    getDocumentationMediaUrl,
} from './documentation';
import { getMetaUploadContentType } from './audioRecorder';
import {
    getAudioUploadContentType,
    normalizeAudioForMetaUpload,
} from './normalizeAudioForMetaUpload';

const IMAGE_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'gif', 'webp']);
const IMAGE_MIME_PREFIX = 'image/';

export const isImagePreview = (previewUrl: string, contentType?: string) => {
    if (contentType?.startsWith(IMAGE_MIME_PREFIX)) return true;

    const extension = previewUrl.split('?')[0].split('.').pop()?.toLowerCase();
    return Boolean(extension && IMAGE_EXTENSIONS.has(extension));
};

export type TOutboundPreviewMediaType =
    | 'image'
    | 'pdf'
    | 'video'
    | 'audio'
    | 'unsupported';

const AUDIO_EXTENSIONS = new Set([
    'ogg',
    'opus',
    'mp3',
    'm4a',
    'aac',
    'amr',
    'wav',
]);

export const getOutboundPreviewMediaType = (
    name: string,
    contentType?: string,
    source?: string
): TOutboundPreviewMediaType => {
    if (contentType?.startsWith(IMAGE_MIME_PREFIX)) return 'image';
    if (contentType === 'application/pdf') return 'pdf';
    if (contentType?.startsWith('video/')) return 'video';
    if (contentType?.startsWith('audio/')) return 'audio';

    const extension =
        name.split('.').pop()?.toLowerCase() ||
        source?.split('?')[0].split('.').pop()?.toLowerCase();

    if (extension && IMAGE_EXTENSIONS.has(extension)) return 'image';
    if (extension === 'pdf') return 'pdf';
    if (extension === 'mp4') return 'video';
    if (extension && AUDIO_EXTENSIONS.has(extension)) return 'audio';

    return 'unsupported';
};

export const getLocalFileId = (file: File) => `local-${generateFileKey(file)}`;

export const getLocalFilePreviewUrl = (
    file: File,
    previewUrlCache: Map<string, string>
) => {
    const key = generateFileKey(file);

    if (!previewUrlCache.has(key)) {
        previewUrlCache.set(key, URL.createObjectURL(file));
    }

    return previewUrlCache.get(key)!;
};

export const revokeLocalFilePreviewUrl = (
    file: File,
    previewUrlCache: Map<string, string>
) => {
    const key = generateFileKey(file);
    const previewUrl = previewUrlCache.get(key);

    if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
        previewUrlCache.delete(key);
    }
};

export const revokeAllLocalFilePreviewUrls = (
    previewUrlCache: Map<string, string>
) => {
    previewUrlCache.forEach((previewUrl) => {
        URL.revokeObjectURL(previewUrl);
    });
    previewUrlCache.clear();
};

export const toOutboundItemFromDocumentation = (
    document: TDocumentation
): TOutboundMultimediaItem => ({
    id: `doc-${document._id}`,
    source: 'documentation',
    name: document.name,
    previewUrl: getDocumentationMediaUrl(document.file),
    contentType: getDocumentationContentType(document.file),
    documentation: document,
});

export const toOutboundItemFromLocalFile = (
    file: File,
    previewUrlCache: Map<string, string>
): TOutboundMultimediaItem => ({
    id: getLocalFileId(file),
    source: 'local',
    name: file.name,
    previewUrl: getLocalFilePreviewUrl(file, previewUrlCache),
    contentType: file.type,
    localFile: file,
});

export const buildOutboundPreviewItems = (
    documentation: TDocumentation[],
    localFiles: File[],
    previewUrlCache: Map<string, string>
): TOutboundMultimediaItem[] => [
    ...documentation.map(toOutboundItemFromDocumentation),
    ...localFiles.map((file) =>
        toOutboundItemFromLocalFile(file, previewUrlCache)
    ),
];

export const getDocumentationSendMedia = (document: TDocumentation) => ({
    url: getDocumentationMediaUrl(document.file),
    contentType: getDocumentationContentType(document.file),
});

export const getImageContentTypeFromUrl = (url: string): string => {
    const extension = url.split('?')[0].split('.').pop()?.toLowerCase();

    if (extension === 'jpg' || extension === 'jpeg') return 'image/jpeg';
    if (extension && IMAGE_EXTENSIONS.has(extension)) {
        return `image/${extension}`;
    }

    return 'image/jpeg';
};

export const getPresentationCardSendMedia = (url: string) => ({
    url,
    contentType: getImageContentTypeFromUrl(url),
});

export const getMediaFileNameFromSource = (source: string) => {
    const path = source.split('?')[0];
    return path.split('/').pop() || 'file';
};

export type TMultimediaPreview = {
    id?: string;
    source: string;
    name: string;
    contentType?: string;
};

export const resolveOutboundPreviewSource = (item: TOutboundMultimediaItem) => {
    if (item.source === 'local') {
        return item.previewUrl;
    }

    if (item.documentation?.file) {
        return getDocumentationMediaUrl(item.documentation.file);
    }

    return getDocumentationMediaUrl(item.previewUrl);
};

export const toMultimediaPreviewFromOutboundItem = (
    item: TOutboundMultimediaItem
): TMultimediaPreview => ({
    id: item.id,
    source: resolveOutboundPreviewSource(item),
    name: item.name,
    contentType: item.contentType,
});

export const toMultimediaPreviewFromMessage = (message: {
    id?: string;
    mediaUrl: string;
    mediaContentType?: string;
    name?: string;
}): TMultimediaPreview => ({
    id: message.id,
    source: message.mediaUrl,
    name: message.name || getMediaFileNameFromSource(message.mediaUrl),
    contentType: message.mediaContentType,
});

export const uploadLocalFileForMessage = async (file: File) => {
    const isAudio = file.type.toLowerCase().startsWith('audio/');

    const uploadFile = isAudio ? await normalizeAudioForMetaUpload(file) : file;

    if (isAudio) {
        const uploadContentType = getAudioUploadContentType(uploadFile);
        const normalizedFile =
            uploadContentType !== uploadFile.type
                ? new File([uploadFile], uploadFile.name, {
                      type: uploadContentType,
                  })
                : uploadFile;

        const { key } = await uploadImageToS3(normalizedFile);

        return {
            url: `${AWS_S3}${key}`,
            contentType: uploadContentType,
        };
    }

    const uploadContentType =
        getMetaUploadContentType(uploadFile.type) ||
        uploadFile.type.split(';')[0]?.trim().toLowerCase() ||
        uploadFile.type;

    const normalizedFile =
        uploadContentType !== uploadFile.type
            ? new File([uploadFile], uploadFile.name, {
                  type: uploadContentType,
              })
            : uploadFile;

    const { key } = await uploadImageToS3(normalizedFile);

    return {
        url: `${AWS_S3}${key}`,
        contentType: uploadContentType,
    };
};
